import { useState, useEffect, useCallback } from 'react';
import { Box, Fade, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LsAlert, LsButton, LsCard } from '../../../../core';
import { getMyAllSubmission } from '../../../../service/api/FormSubmissionService';
import { useMySubmissionColumns } from './mySubmissionsColumns';
import SubmissionDetailModal from './SubmissionDetailModal';
import MySubmissionsFilters from './MySubmissionsFilters';
import SubmissionsDataGrid from './SubmissionDataGrid';
import {useNavigate} from "react-router-dom";
import wizardApi from "../../../../service";

// ── Layout ───────────────────────────────────────────────────
const PageContainer = styled(Box)(() => ({
    maxWidth: 1536,
    margin: '0 auto',
    padding: '24px 32px',
}));

// ── Component ────────────────────────────────────────────────
const MySubmissionsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [formOptions, setFormOptions] = useState([]);
    const navigate = useNavigate();

    // Pagination
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });

    // Filters
    const [formFilter, setFormFilter] = useState('');

    // ── Filter handlers ──────────────────────────────────────
    const handleFormFilterChange = (e) => {
        setFormFilter(e.target.value);
        setPaginationModel((p) => ({ ...p, page: 0 }));
    };

    // ── Data fetching ────────────────────────────────────────
    const fetchSubmissions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { page, pageSize } = paginationModel;
            const filters = {};
            if (formFilter) filters.formId = formFilter;

            const data = await getMyAllSubmission(page, pageSize, filters);
            console.log(data);

            setRows(data.content);
            setRowCount(data.totalElements);

            // Accumulate unique form options from results
            setFormOptions((prev) => {
                const existing = new Map(prev.map((o) => [o.value, o]));
                data.content.forEach((item) => {
                    if (item.formId && !existing.has(item.formId)) {
                        existing.set(item.formId, {
                            value: item.formId,
                            label: item.formName || item.formId,
                        });
                    }
                });
                return Array.from(existing.values());
            });
        } catch (err) {
            setError(typeof err === 'string' ? err : err?.message || 'Failed to load submissions.');
        } finally {
            setLoading(false);
        }
    }, [paginationModel, formFilter]);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    // ── Detail modal state ───────────────────────────────────
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

    // ── Row action handlers ──────────────────────────────────
    const handleView = useCallback((row) => {
        setSelectedSubmissionId(row.id);
        setDetailOpen(true);
    }, []);

    const handleEdit = useCallback(async (row) => {
        try {
            const summaries = await wizardApi.getWizardSummaries();
            const wizard = summaries.find(w => w.id === row.formId);

            if (!wizard) {
                window.alert(`Wizard not found for formId: ${row.formId}`);
                return;
            }

            // wizard.url is like "/f2f98bd9-..." — strip leading slash for the slug segment
            const urlSlug = wizard.url.replace(/^\//, '');

            navigate(`/forms/active/${row.formId}/${urlSlug}/${row.id}`);
        } catch (err) {
            console.error('Failed to resolve wizard URL for edit:', err);
        }
    }, [navigate]);

    // ── Columns ──────────────────────────────────────────────
    const columns = useMySubmissionColumns({ onView: handleView, onEdit: handleEdit });

    return (
        <PageContainer>
            {loading && (
                <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
            )}

            <Fade in timeout={600}>
                <Box>
                    {/* ── Page header ──────────────────────────────── */}
                    <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ fontSize: '1.25rem' }}>
                                My Submissions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                View and manage your contested case form submissions
                            </Typography>
                        </Box>
                        <LsButton
                            variant="outlined"
                            color="inherit"
                            startIcon={<RefreshIcon />}
                            onClick={fetchSubmissions}
                            loading={loading}
                            size="small"
                        >
                            Refresh
                        </LsButton>
                    </Box>

                    {/* ── Error banner ──────────────────────────────── */}
                    {error && (
                        <Box mb={2}>
                            <LsAlert severity="error" title="Failed to load submissions" onClose={() => setError(null)}>
                                {error}
                            </LsAlert>
                        </Box>
                    )}

                    {/* ── Filters ───────────────────────────────────── */}
                    <MySubmissionsFilters
                        formFilter={formFilter}
                        formOptions={formOptions}
                        onFormFilterChange={handleFormFilterChange}
                    />

                    {/* ── Data grid ─────────────────────────────────── */}
                    <LsCard
                        title="Submissions"
                        subheader={rowCount > 0 ? `${rowCount} total record${rowCount !== 1 ? 's' : ''}` : undefined}
                        elevation={1}
                        sx={{ overflow: 'hidden' }}
                    >
                        <Box sx={{ mx: -2, mb: -2 }}>
                            <SubmissionsDataGrid
                                rows={rows}
                                columns={columns}
                                loading={loading}
                                rowCount={rowCount}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                            />
                        </Box>
                    </LsCard>
                </Box>
            </Fade>

            {/* ── Detail modal ──────────────────────────────────── */}
            <SubmissionDetailModal
                open={detailOpen}
                submissionId={selectedSubmissionId}
                onClose={() => setDetailOpen(false)}
            />
        </PageContainer>
    );
};

export default MySubmissionsPage;
